/**
 * 帳票作成 一覧・作成（S-14・S-63・S-64・S-70・S-71）。担当: G4
 * kintone 版の「帳票作成」（作成条件 → 作成済み一覧 → 詳細で確認・編集 → 出力）を共通画面で再現する。
 */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FilePlus2, FileSpreadsheet } from 'lucide-react';
import {
  Button,
  Checkbox,
  DataTable,
  DateField,
  EmptyState,
  ErrorState,
  FormField,
  PageHeader,
  Panel,
  RadioGroup,
  useConfirm,
  useToast,
} from '@/components';
import { hasRole } from '@/auth/types';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { BusinessOperatorSelect, useVesselOptions } from '@/features/masters';
import { ApiError, errorMessage } from '@/lib/api';
import { useDeleteSnapshot, useGenerateSnapshot, useSnapshots, type GenerateInput } from './api';
import type { FormTypeConfig } from './config';
import './forms.css';

const QUARTERS = ['第1四半期', '第2四半期', '第3四半期', '第4四半期'] as const;

/** 除外船（監視実績報告書②） */
function ExcludeVessels({ year, operatorId, value, onChange }: { year: number; operatorId: number | null; value: number[]; onChange: (v: number[]) => void }) {
  const { data } = useVesselOptions({ fiscalYear: year, businessOperatorId: operatorId }, operatorId != null);
  if (operatorId == null) return <p className="bs-forms-hint">事業実施者を選択すると参加船が表示されます。</p>;
  const list = data ?? [];
  return (
    <div className="bs-forms-exclude" role="group" aria-label="出力対象から除外する船">
      {list.length === 0 && <span className="bs-forms-hint">参加船がありません</span>}
      {list.map((v) => (
        <Checkbox
          key={v.id}
          label={`${v.name}（${v.registrationNo}）`}
          checked={value.includes(v.id)}
          onChange={(e) => onChange(e.target.checked ? [...value, v.id] : value.filter((x) => x !== v.id))}
        />
      ))}
    </div>
  );
}

/** 同一条件の帳票が既にある（409）場合の既存 ID */
export function duplicatedSnapshotId(e: unknown): number | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const id = (e.details as { existingId?: unknown } | undefined)?.existingId;
  return typeof id === 'number' ? id : null;
}

function CreatePanel({ config, onShowExisting }: { config: FormTypeConfig; onShowExisting: (businessOperatorId: number) => void }) {
  const { year, fiscalYear, yearPath } = useFiscalYear();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const generate = useGenerateSnapshot();
  const remove = useDeleteSnapshot();
  const [operatorId, setOperatorId] = useState<number | null>(null);
  const [baseDate, setBaseDate] = useState('');
  const [variant, setVariant] = useState<string>(config.createKind === 'dispatch' ? '第1四半期' : '計画');
  const [from, setFrom] = useState(config.createKind === 'monitoring2' || config.createKind === 'monitoring3' ? fiscalYear.startDate : '');
  const [to, setTo] = useState(config.createKind === 'monitoring2' || config.createKind === 'monitoring3' ? fiscalYear.endDate : '');
  const [exclude, setExclude] = useState<number[]>([]);
  const [fieldError, setFieldError] = useState<Record<string, string>>({});

  const onCreate = async () => {
    const errs: Record<string, string> = {};
    if (operatorId == null) errs.businessOperatorId = '事業実施者を選択してください';
    if (config.createKind === 'roster' && !baseDate) errs.baseDate = '基準日は必須です';
    if (config.createKind === 'monitoring2' && (!from || !to)) errs.period = '調査日は必須です';
    if (config.createKind === 'monitoring3' && (!from || !to)) errs.period = '操業期間は必須です';
    if (from && to && from > to) errs.period = '期間開始日は期間終了日より前の日付を指定してください';
    setFieldError(errs);
    if (Object.keys(errs).length) return;
    const input: GenerateInput = { type: config.type, fiscalYear: year, businessOperatorId: operatorId };
    if (config.createKind === 'roster') Object.assign(input, { baseDate, variant });
    if (config.createKind === 'dispatch') Object.assign(input, { variant });
    if (config.createKind === 'monitoring2') Object.assign(input, { periodFrom: from, periodTo: to, excludeVesselIds: exclude });
    if (config.createKind === 'monitoring3') Object.assign(input, { periodFrom: from, periodTo: to });
    try {
      const r = await generate.mutateAsync(input);
      toast.success('帳票作成が完了しました。');
      navigate(yearPath(`${config.path}/${r.id}`));
    } catch (e) {
      const existingId = config.createKind === 'monitoring1' ? duplicatedSnapshotId(e) : null;
      if (existingId == null) {
        toast.error('帳票を作成できませんでした', errorMessage(e));
        return;
      }
      // kintone 検証指摘（R08.10.14 様式（実績報告書①）作成画面）：既にある場合は更新（削除→再作成）するか確認する
      const ok = await confirm({
        title: `${config.title}は既にありますが、更新しますか？`,
        message: '「はい」：現在の帳票データを削除して、最新のデータで作り直します（編集した特記事項も作り直されます）。\n「いいえ」：現在の帳票データを一覧に表示します。',
        confirmLabel: 'はい（更新する）',
        cancelLabel: 'いいえ',
      });
      if (!ok) {
        onShowExisting(operatorId!);
        return;
      }
      try {
        await remove.mutateAsync(existingId);
        const r = await generate.mutateAsync(input);
        toast.success('帳票を更新しました。');
        navigate(yearPath(`${config.path}/${r.id}`));
      } catch (e2) {
        toast.error('帳票を更新できませんでした', errorMessage(e2));
      }
    }
  };

  return (
    <Panel title="帳票作成" description="作成条件を指定して「帳票作成」を押すと、その時点のデータで帳票データを作成します（同じ条件で作り直す場合は作成済みデータを削除してください）。">
      <div className="bs-forms-create">
        <FormField label="事業実施者" required error={fieldError.businessOperatorId}>
          <BusinessOperatorSelect fiscalYear={year} value={operatorId} onChange={setOperatorId} placeholder="選択してください" width="full" />
        </FormField>
        {config.createKind === 'roster' && (
          <>
            <FormField label="基準日" required error={fieldError.baseDate}>
              <DateField showWareki value={baseDate} onChange={(e) => setBaseDate(e.target.value)} aria-label="基準日" />
            </FormField>
            <FormField label="計画／実績" required>
              <RadioGroup
                name="variant"
                value={variant}
                onChange={setVariant}
                options={[
                  { value: '計画', label: '計画（参加船名簿）' },
                  { value: '実績', label: '実績（速報）' },
                ]}
              />
            </FormField>
          </>
        )}
        {config.createKind === 'dispatch' && (
          <FormField label="作成の条件" required>
            <RadioGroup name="quarter" direction="column" value={variant} onChange={setVariant} options={QUARTERS.map((q) => ({ value: q, label: `${q}までの実績を出力する。` }))} />
          </FormField>
        )}
        {(config.createKind === 'monitoring2' || config.createKind === 'monitoring3') && (
          <FormField label={config.createKind === 'monitoring2' ? '調査日' : '操業期間'} required span={2} error={fieldError.period}>
            <div className="bs-forms-period">
              <DateField value={from} onChange={(e) => setFrom(e.target.value)} aria-label="期間（開始）" />
              <span>〜</span>
              <DateField value={to} onChange={(e) => setTo(e.target.value)} aria-label="期間（終了）" />
            </div>
          </FormField>
        )}
        {config.createKind === 'monitoring1' && <p className="bs-forms-hint">対象期間：{fiscalYear.startDate} 〜 {fiscalYear.endDate}（年度全体）</p>}
        {config.createKind === 'monitoring2' && (
          <FormField label="出力対象から除外する船" hint="チェックした船は帳票に含めません">
            <ExcludeVessels year={year} operatorId={operatorId} value={exclude} onChange={setExclude} />
          </FormField>
        )}
        <div className="bs-forms-create__actions">
          <Button variant="primary" icon={<FilePlus2 size={16} />} loading={generate.isPending || remove.isPending} onClick={() => void onCreate()}>
            帳票作成
          </Button>
        </div>
      </div>
    </Panel>
  );
}

export function SnapshotListPage({ config }: { config: FormTypeConfig }) {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const { year, yearPath } = useFiscalYear();
  const [params, setParams] = useSearchParams();
  const operatorFilter = params.get('businessOperatorId') ? Number(params.get('businessOperatorId')) : null;
  const filter = useMemo(() => ({ businessOperatorId: operatorFilter }), [operatorFilter]);
  const { data, isPending, error, refetch } = useSnapshots(config.type, year, filter);
  const canEdit = hasRole(user, ['基金', '漁協']);

  return (
    <>
      <PageHeader eyebrow="帳票出力（各種様式）" title={config.title} description={config.description} meta={<span className="bs-forms-ids">{config.screenIds}</span>} />
      {canEdit && (
        <CreatePanel
          config={config}
          onShowExisting={(opId) => {
            const p = new URLSearchParams(params);
            p.set('businessOperatorId', String(opId));
            setParams(p, { replace: true });
          }}
        />
      )}
      <section className="bs-forms-list" aria-label="作成済み一覧">
        <div className="bs-forms-list__head">
          <h2 className="bs-forms-h2">作成済み一覧</h2>
          <div className="bs-forms-list__filter">
            <span>事業実施者</span>
            <BusinessOperatorSelect
              fiscalYear={year}
              value={operatorFilter}
              width="md"
              onChange={(v) => {
                const p = new URLSearchParams(params);
                if (v == null) p.delete('businessOperatorId');
                else p.set('businessOperatorId', String(v));
                setParams(p, { replace: true });
              }}
            />
          </div>
        </div>
        {error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : (
          <DataTable
            caption="作成済み一覧"
            columns={config.listColumns}
            rows={data?.items ?? []}
            rowKey={(r) => r.id}
            loading={isPending}
            pagination={{ pageSize: 30 }}
            onRowClick={(r) => navigate(yearPath(`${config.path}/${r.id}`))}
            empty={<EmptyState compact icon={<FileSpreadsheet />} title="作成済みの帳票はありません" description={canEdit ? '上の「帳票作成」から作成してください。' : undefined} />}
          />
        )}
      </section>
    </>
  );
}
